import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import MobileNumberChecker from "./MobileNumberChecker";
import MobileNumberRequestForm from "./MobileNumberRequestForm";
import "./MobileNumerology.css";
import "./MobileNumerologyPage.css";

const SEO = {
  title: "Mobile Numerology Consultation | Personalised Mobile Number – Vaidik Vidhya™",
  description:
    "Explore personalised mobile number customization with Vaidik Vidhya™. Horoscope analysis, planetary considerations, numerology and a guided SIM delivery process.",
};

const PROCESS = [
  {
    title: "Deep analysis",
    body: "Our expert panel analyses your number chart and your horoscope (Kundli). We study important aspects including shadbala, bhavabala and ashtakavarga to identify the planetary energy that is missing in your growth.",
  },
  {
    title: "Personalised calculation",
    body: "The most important part of the analysis, and the step on which most of the value of your number depends. We aim to activate the right planetary energy in the right house (Bhava) to support wealth, success, harmony, career growth and overall progress. From this we determine your Lucky Total and your Power Pairs.",
  },
  {
    title: "Number selection",
    body: "Using permutations and combinations, our team searches the telecom operator's available database and shortlists only those mobile numbers that match your personalised calculations.",
  },
  {
    title: "Multi-level verification",
    body: "Every shortlisted number goes through strict quality checks. If a number does not meet our standards, the selection is repeated until we find the best possible match for you.",
  },
  {
    title: "Report and home delivery",
    body: "After final approval from our expert panel you receive your personalised hand-typed report, your mobile number is booked, and the SIM is delivered to your doorstep within 24–48 hours through the operator's delivery partner (terms apply).",
  },
];

const START_STEPS = [
  {
    title: "Share your details",
    body: "Fill in the form below with your correct full name, date of birth, time and place of birth, your current city and state, WhatsApp number, email and your main objective.",
  },
  {
    title: "Secure your booking",
    body: "After you submit, you receive an order reference. Complete your payment as guided by our team and share the confirmation quoting that reference. Once verified, our specialists begin the customization process.",
  },
  {
    title: "Customization",
    body: "We customise your number using our exclusive 5-step process described above.",
  },
  {
    title: "Selection and server booking",
    body: "Within 7–10 working days we present your customised number (Saturdays, Sundays and gazetted holidays are counted as off; urgent customization is charged extra). We then book the number on the operator's server using your pin code, delivery address and a currently active mobile number, which must be reachable for calls because the delivery person will call it.",
  },
  {
    title: "Tracking and delivery",
    body: "After booking you receive an SMS on your active number with your unique booking ID, a live tracking link and the delivery assistance contact. The delivery executive handles the official KYC and activation at your doorstep, subject to availability and serviceability (delivery partner: Neolite).",
  },
];

const INCLUDED = [
  "Your astro-numerologically aligned mobile number.",
  "A hand-written report of your number in PDF form.",
  "3 Vaidik remedies to help rectify previous karmic patterns, worth ₹1,999.",
  "A Vastu-shuddhi PDF to cleanse and magnify the energy of your living space, worth ₹999.",
  "One 4-digit PIN and one 6-digit PIN password, worth ₹999.",
];

const NUMBER_PROMISES = [
  "Without 2, 4 and 8.",
  "Without 0 in the last 6 digits.",
  "With at least one Raj Yog in the number.",
  "Without negative combinations.",
  "Compatible with your personalised calculations.",
  "Aimed at activating the energy of the right planet in the right house of your horoscope.",
];

const FAQ = [
  ["What is mobile numerology?", "Mobile numerology is a traditional numerological practice in which the digits and patterns of a mobile number are interpreted for their symbolic associations."],
  ["How does Vaidik Vidhya™ select a personalised mobile number?", "We begin with your birth details and Ascendant Horoscope, examine relevant planetary placements and evaluate number patterns through our numerology framework before recommending a suitable number, subject to availability."],
  ["Can I get a mobile number based on my horoscope?", "You can request a personalised consultation in which your horoscope and number chart are considered during the selection process."],
  ["Can a mobile number guarantee success in business or career?", "No. A mobile number cannot guarantee success or any specific life outcome. Our service offers personalised guidance based on traditional astrological and numerological interpretations."],
  ["How do I order a personalised mobile number?", "Complete the enquiry form, provide the requested birth and contact details, review the applicable service and charges, and proceed through the confirmed payment and order process."],
  ["How will I receive my SIM?", "Once the number and order are confirmed and the required operator formalities are completed, SIM delivery can be arranged through the designated operator's delivery partner, Neolite, subject to availability and serviceability."],
  ["How long does delivery take?", "Delivery timelines depend on the operator, location, verification requirements and logistics. The applicable estimated timeline will be communicated when the order is confirmed."],
  ["What information do I need for mobile number customization?", "Typically your date of birth, time of birth, place of birth, contact details and primary objective are required. Additional information may be requested where necessary."],
];

const LINKS = [
  ["Astrology Consultation", "/consultation/astrology"],
  ["Numerology Consultation", "/consultation/numerology"],
  ["Birth Chart Analysis", "/consultation/astrology"],
  ["Contact Us", "/contact"],
  ["Privacy Policy", "/privacy-policy"],
  ["Terms and Conditions", "/terms-and-conditions"],
  ["Refund and Cancellation Policy", "/refund-Policy"],
  ["Mobile Numerology Policy", "/mobile-numerology-policy"],
];

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

// Sets the page title/description/canonical and FAQ structured data, and restores them on leave.
function useSeo() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = SEO.title;

    let meta = document.querySelector('meta[name="description"]');
    const createdMeta = !meta;
    if (createdMeta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const previousDescription = meta.getAttribute("content");
    meta.setAttribute("content", SEO.description);

    const canonical = document.createElement("link");
    canonical.rel = "canonical";
    canonical.href = `${window.location.origin}/mobile-numerology`;
    document.head.appendChild(canonical);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    });
    document.head.appendChild(ld);

    return () => {
      document.title = previousTitle;
      if (createdMeta) meta.remove();
      else meta.setAttribute("content", previousDescription || "");
      canonical.remove();
      ld.remove();
    };
  }, []);
}

// Jump to #checker / #customize when arriving with a hash (e.g. after sign-in).
function useHashScroll() {
  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (id) setTimeout(() => scrollTo(id), 150);
  }, []);
}

export default function MobileNumerologyLanding() {
  useSeo();
  useHashScroll();

  return (
    <>
      <Header />
      <main className="mnp">
        {/* ------------------------------------------------------------ hero */}
        <section className="mnp-hero">
          <div className="mnp-wrap">
            <span className="mnp-eyebrow">Vaidik Vidhya™ · An initiative of AstroLogical Solutions™</span>
            <h1>Mobile Numerology Consultation</h1>
            <p className="mnp-lead">
              A personalised mobile number, shortlisted from your Ascendant Horoscope and your numerological profile - with guided SIM delivery to your doorstep.
            </p>
            <button type="button" className="mnr-btn mnr-btn-gold mnp-cta" onClick={() => scrollTo("customize")}>
              Start Your Mobile Number Customization
            </button>
            <p className="mnp-support">Share your birth details to begin your personalised horoscope and number analysis.</p>
            <button type="button" className="mnp-textlink" onClick={() => scrollTo("checker")}>
              Or check any mobile number for free ↓
            </button>
          </div>
        </section>

        {/* ----------------------------------------------------------- intro */}
        <section className="mnp-section">
          <div className="mnp-wrap mnp-narrow">
            <p>
              <strong>AstroLogical Solutions™</strong> is a Government of India, MSME and ISO 9001:2015 registered organization. Under the banner of
              AstroLogical Solutions™, Vaidik Vidhya™ provides consultations related to Astrology, Vastu and Numerology.
            </p>
            <p>
              As numerologists, Vastu consultants and astrologers, we do not merely perform generic calculations; we also look at the specific houses in
              your horoscope that support prosperity for you. The combination of planetary considerations and the right mobile number is a thoughtful
              step towards the change you have been waiting for.
            </p>
          </div>
        </section>

        {/* --------------------------------------------------- free checker */}
        <section className="mnp-section mnp-alt" id="checker">
          <div className="mnp-wrap">
            <h2>Check a mobile number - free</h2>
            <p className="mnp-sub">
              See the total, birth and destiny numbers, pairs and the Vedic grid for any number. For a new number, avoid 4 and 8 and choose a total of 1,
              3, 5 or 6 that is friendly to both of your numbers.
            </p>
            <MobileNumberChecker />
          </div>
        </section>

        {/* --------------------------------------------------------- process */}
        <section className="mnp-section">
          <div className="mnp-wrap">
            <h2>How we create your personalised mobile number</h2>
            <ol className="mnp-steps">
              {PROCESS.map((s, i) => (
                <li key={s.title}>
                  <span className="mnp-step-no">Step {i + 1}</span>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </li>
              ))}
            </ol>
            <blockquote className="mnp-quote">
              “After switching to my Vedic mobile number, my business doubled within 6 months.”
              <cite>- Vinisha Jagdeep, Chartered Accountant</cite>
            </blockquote>
            <div className="mnp-center">
              <button type="button" className="mnr-btn mnr-btn-gold" onClick={() => scrollTo("customize")}>
                Book Consultation
              </button>
              <a
                className="mnr-btn mnr-btn-line"
                href={`https://wa.me/917351519573?text=${encodeURIComponent("Hello, I have a question about Mobile Numerology.")}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Have a question? WhatsApp us
              </a>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------ how to start */}
        <section className="mnp-section mnp-alt">
          <div className="mnp-wrap">
            <h2>How to start - get your exclusive lucky mobile number</h2>
            <p className="mnp-sub">Follow these 5 simple steps to secure a customised mobile number that aligns with your date of birth and your horoscope.</p>
            <ol className="mnp-steps">
              {START_STEPS.map((s, i) => (
                <li key={s.title}>
                  <span className="mnp-step-no">Step {i + 1}</span>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </li>
              ))}
            </ol>
            <ul className="mnp-notes">
              <li>
                <strong>Tracking:</strong>{" "}
                <a className="mnr-btn mnr-btn-line mnp-small" href="https://www.myvi.in/new-connection/track-your-sim-online" target="_blank" rel="noopener noreferrer">
                  Track your SIM online
                </a>
              </li>
              <li>
                <strong>Dedicated support:</strong> delivery assistance team on <a href="tel:8141018180">8141018180</a> (9 AM – 9 PM).
              </li>
              <li>We deliver your customised SIM anywhere in India free of delivery charges. Standard activation and recharge charges apply.</li>
            </ul>
          </div>
        </section>

        {/* -------------------------------------------------------- why us */}
        <section className="mnp-section">
          <div className="mnp-wrap mnp-narrow">
            <h2>Why choose Vaidik Vidhya™?</h2>
            <p>If you have a concern, we have an astro-numerology based approach waiting for you.</p>
            <p>
              At Vaidik Vidhya™, your mobile number is not selected on the basis of a generic “lucky number methodology”. We study your Ascendant (Lagna)
              horoscope, ashtakavarga, bhavabala and shadbala to identify the planetary energies that need to be strengthened or supported for your specific
              requirements.
            </p>
            <p className="mnp-formula">Right planetary energy 👉 in the right house of the horoscope 👉 a result-oriented number.</p>
            <p>
              We customise the numerical pattern of your mobile number with the objective of activating and supporting the energy of the relevant planet in
              the appropriate house of your Ascendant horoscope. Whether your concern relates to career, business, finances, relationships, communication,
              stability or growth, our approach begins with your horoscope - not with a pre-defined generic technique.
            </p>
            <p>
              <strong>Don&apos;t choose a number randomly. Choose a Vaidik Vidhya™ number.</strong>
            </p>
          </div>
        </section>

        {/* ----------------------------------------------- inclusions + price */}
        <section className="mnp-section mnp-alt">
          <div className="mnp-wrap mnp-two">
            <div>
              <h2>What you will get in our consultation</h2>
              <ol className="mnp-list">
                {INCLUDED.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ol>
              <p className="mnp-fine">The bonuses in points 3, 4 and 5 are available only if you book today.</p>
            </div>
            <div className="mnp-price">
              <span className="mnp-badge">Limited period offer</span>
              <h2>Consultation charges</h2>
              <p className="mnp-amount">
                <s>₹5,100</s> <s>₹3,100</s> <strong>₹1,999</strong> <small>(+ GST)</small>
              </p>
              <h3>Your number will be</h3>
              <ul className="mnp-list">
                {NUMBER_PROMISES.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
              <p className="mnp-fine">
                After 11 bookings the price returns to the regular price. The number is delivered to you within 7–10 working days after customization. Once this
                offer is missed, it will not be offered again.
              </p>
              <button type="button" className="mnr-btn mnr-btn-gold" onClick={() => scrollTo("customize")}>
                Book your number now
              </button>
            </div>
          </div>
        </section>

        {/* ------------------------------------------------------------ form */}
        <section className="mnp-section" id="customize">
          <div className="mnp-wrap mnp-narrow">
            <h2>Take action - get your personalised mobile number</h2>
            <p className="mnp-sub">
              You have learned about our approach. Now take the first step towards a mobile number recommendation based on your Ascendant Horoscope and
              numerological profile. Tell us about yourself, share your primary objective and submit your request. Our team will guide you through the
              applicable consultation, payment and number selection process.
            </p>
            <h3 className="mnp-form-title">Mobile Number Customization Form</h3>
            <MobileNumberRequestForm />
          </div>
        </section>

        {/* ------------------------------------------------------------- FAQ */}
        <section className="mnp-section mnp-alt">
          <div className="mnp-wrap mnp-narrow">
            <h2>Frequently asked questions</h2>
            <div className="mnp-faq">
              {FAQ.map(([q, a]) => (
                <details key={q}>
                  <summary>{q}</summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ----------------------------------------------------- links + note */}
        <section className="mnp-section">
          <div className="mnp-wrap mnp-narrow">
            <nav className="mnp-links" aria-label="Related pages">
              {LINKS.map(([label, to]) => (
                <Link key={label} to={to}>
                  {label}
                </Link>
              ))}
            </nav>
            <p className="mnp-disclaimer">
              Astrology and numerology are traditional interpretive practices. A mobile number does not guarantee any particular personal, professional,
              financial or other outcome. Please read our{" "}
              <Link to="/mobile-numerology-policy">Mobile Numerology Consultation Policy</Link> before booking.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
